import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatButtonModule } from '@angular/material/button'
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MAP } from '@bases/base.token'
import { ToasterService } from '@core/services/toaster.service'
import { environment } from '@env/environment'
import * as L from 'leaflet'
import { OpenStreetMapProvider } from 'leaflet-geosearch'
import { SearchResult } from 'leaflet-geosearch/dist/providers/provider.js'
import { MatomoTracker } from 'ngx-matomo-client'
import { BehaviorSubject } from 'rxjs'
import { debounceTime, switchMap } from 'rxjs/operators'
import { SearchBarService } from './searchbar.service'

@Component({
  selector: 'app-search-bar',
  templateUrl: './searchbar.component.html',
  styleUrls: ['./searchbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    // Forms
    FormsModule,
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,

    // Material
    MatTooltipModule,
    MatFormField,
    MatButtonModule,
    MatIconModule,
    MatAutocompleteModule,
  ],
})
export class SearchBarComponent {
  private readonly _map = inject(MAP) as BehaviorSubject<L.DrawMap>
  private readonly _searchBarService = inject(SearchBarService)
  private readonly _tracker = inject(MatomoTracker)
  private readonly _toasterService = inject(ToasterService)
  searchControl = new FormControl('')
  autoCompleteResults$ = signal<SearchResult[]>([])

  private readonly provider = new OpenStreetMapProvider({
    params: {
      'accept-language': 'en',
      countrycodes: environment.countryCodes,
    },
  })

  constructor() {
    this._initAutoComplete()
  }

  private _initAutoComplete() {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        switchMap((value) => this.provider.search({ query: (value ?? '').trim() })),
      )
      .subscribe({
        next: (searchResults) => {
          this.autoCompleteResults$.set(searchResults)
        },
        error: (err) => {
          console.error(err)
        },
      })
  }

  goToSearchedPlace() {
    const address = this.searchControl.value
    if (!address) {
      return
    }

    this._tracker.trackEvent('Search', 'Use searchbar', address)

    this._searchBarService.geocode(address).subscribe({
      next: (location) => {
        this._map.getValue().fitBounds(location.viewBounds, {})
      },
      error: (error) => {
        console.warn('goToSearchedPlace error', error)
        this._toasterService.openSnackBarError('Moving to the desired location did not work.')
      },
    })
  }

  clearSearch() {
    this.searchControl.setValue('')
  }

  onOptionSelected(label: string) {
    this.searchControl.setValue(label, { emitEvent: false })
    this.goToSearchedPlace()
  }
}
